/**
 * <pdp-gallery>
 *
 * Hero gallery for the PDP main image. Renders every curated_gallery image as
 * a [data-role="hero-slide"]; desktop toggles the --active class to show one
 * slide at a time; mobile turns the track into a horizontal scroll-snap
 * carousel where the next slide peeks on the right.
 *
 * Selectors:
 *   - [data-role="hero-track"]  — the (scrollable on mobile) slide container
 *   - [data-role="hero-slide"]  — each slide; carries data-index
 *   - [data-role="thumb"]       — clickable thumbnail buttons; carry data-index
 *   - [data-role="prev"] / [data-role="next"] — arrow buttons over hero (desktop)
 *
 * Initial selected index comes from the host's data-default-index attr (defaults to 0).
 */
(() => {
  if (customElements.get('pdp-gallery')) return;

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.heroTrack  = this.querySelector('[data-role="hero-track"]');
      this.heroSlides = Array.from(this.querySelectorAll('[data-role="hero-slide"]'));
      this.thumbs    = Array.from(this.querySelectorAll('[data-role="thumb"]'));
      this.prevBtn   = this.querySelector('[data-role="prev"]');
      this.nextBtn   = this.querySelector('[data-role="next"]');

      if (this.heroSlides.length === 0) return;

      const def = parseInt(this.dataset.defaultIndex, 10);
      this.current = Number.isFinite(def) ? def : 0;
      this._suppressScrollSync = false;
      this._scrollResetTimer = null;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const idx = parseInt(thumb.dataset.index, 10);
          if (Number.isFinite(idx)) this.select(idx);
        });
      });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.select(this.current - 1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.select(this.current + 1));
      }

      // Mobile carousel: sync active slide from scroll position
      if (this.heroTrack) {
        let scrollDebounce;
        this.heroTrack.addEventListener('scroll', () => {
          if (this._suppressScrollSync) return;
          clearTimeout(scrollDebounce);
          scrollDebounce = setTimeout(() => this.syncFromScroll(), 80);
        }, { passive: true });
      }

      this.syncSelectedState();
    }

    select(i) {
      const n = this.heroSlides.length;
      if (n === 0) return;
      // Wrap around at both ends.
      i = ((i % n) + n) % n;
      this.current = i;

      const slide = this.heroSlides[i];
      if (slide) {
        // Promote lazy slides to eager once they're shown.
        const img = slide.querySelector('img');
        if (img && img.loading === 'lazy') img.loading = 'eager';

        // If the track is a horizontal scroller (mobile), scroll to this slide.
        if (this.heroTrack && this.isHorizontalScroll()) {
          this._suppressScrollSync = true;
          const target = slide.offsetLeft - this.heroTrack.offsetLeft;
          this.heroTrack.scrollTo({ left: target, behavior: 'smooth' });
          clearTimeout(this._scrollResetTimer);
          this._scrollResetTimer = setTimeout(() => {
            this._suppressScrollSync = false;
          }, 500);
        }
      }

      this.syncSelectedState();
    }

    isHorizontalScroll() {
      if (!this.heroTrack) return false;
      return this.heroTrack.scrollWidth > this.heroTrack.clientWidth + 1;
    }

    syncFromScroll() {
      if (!this.heroTrack || !this.isHorizontalScroll()) return;
      const trackScroll = this.heroTrack.scrollLeft;
      let closest = 0;
      let minDistance = Infinity;
      this.heroSlides.forEach((slide, idx) => {
        const slideOffset = slide.offsetLeft - this.heroTrack.offsetLeft;
        const distance = Math.abs(slideOffset - trackScroll);
        if (distance < minDistance) {
          minDistance = distance;
          closest = idx;
        }
      });
      if (closest !== this.current) {
        this.current = closest;
        this.syncSelectedState();
      }
    }

    syncSelectedState() {
      this.heroSlides.forEach((s, idx) => {
        s.classList.toggle('salt-pdp-main__hero-slide--active', idx === this.current);
      });
      this.thumbs.forEach((t, idx) => {
        t.classList.toggle('salt-pdp-main__thumb--selected', idx === this.current);
      });
    }
  }

  customElements.define('pdp-gallery', PdpGallery);
})();
